import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ToastService } from './toast.service';
import { BehaviorSubject } from 'rxjs';

/**
 * Servicio para manejar errores de carga de módulos dinámicos
 */
@Injectable({
  providedIn: 'root',
})
export class ModuleLoadingErrorService {
  private errorCountSubject = new BehaviorSubject<number>(0);
  public errorCount$ = this.errorCountSubject.asObservable();

  private maxRetries = 3;
  private retryDelay = 2000; // 2 segundos
  private moduleRetryCount = new Map<string, number>();

  constructor(private router: Router, private toastService: ToastService) {
    // Interceptar errores de carga de chunks
    this.setupChunkErrorHandler();
  }

  /**
   * Configura el manejador global de errores de chunks
   */
  private setupChunkErrorHandler(): void {
    const originalConsoleError = console.error;

    // Interceptar errores de consola relacionados con chunks
    console.error = (...args: any[]) => {
      const errorMessage = args[0]?.toString() || '';

      if (this.isChunkLoadError(errorMessage)) {
        this.handleChunkLoadError(errorMessage);
      }

      // Llamar al console.error original
      originalConsoleError.apply(console, args);
    };

    // Interceptar errores no capturados
    window.addEventListener('error', (event) => {
      if (this.isChunkLoadError(event.message)) {
        this.handleChunkLoadError(event.message);
      }
    });

    // Interceptar errores de promesas no capturadas
    window.addEventListener('unhandledrejection', (event) => {
      const errorMessage = event.reason?.toString() || '';
      if (this.isChunkLoadError(errorMessage)) {
        this.handleChunkLoadError(errorMessage);
        event.preventDefault(); // Prevenir que se muestre en consola
      }
    });
  }

  /**
   * Verifica si un error es relacionado con la carga de chunks
   * @param errorMessage Mensaje de error
   * @returns true si es un error de carga de chunks
   */
  private isChunkLoadError(errorMessage: string): boolean {
    const chunkErrorPatterns = [
      'Failed to fetch dynamically imported module',
      'Loading chunk',
      'ChunkLoadError',
      'Loading CSS chunk',
      'chunk-',
      '.js net::ERR_',
      'Loading module from',
      'Failed to import',
    ];

    return chunkErrorPatterns.some((pattern) =>
      errorMessage.toLowerCase().includes(pattern.toLowerCase())
    );
  }

  /**
   * Maneja errores de carga de chunks
   * @param errorMessage Mensaje de error
   */
  private handleChunkLoadError(errorMessage: string): void {
    const currentRoute = this.router.url;
    const retryCount = this.moduleRetryCount.get(currentRoute) || 0;

    this.errorCountSubject.next(this.errorCountSubject.value + 1);

    if (retryCount < this.maxRetries) {
      this.moduleRetryCount.set(currentRoute, retryCount + 1);

      // Mostrar toast de reintento
      this.toastService.warning(
        `Error cargando módulo. Reintentando... (${retryCount + 1}/${
          this.maxRetries
        })`,
        3000
      );

      // Reintentar después del retraso
      setTimeout(() => {
        this.retryModuleLoad(currentRoute);
      }, this.retryDelay);
    } else {
      // Máximo de reintentos alcanzado
      this.moduleRetryCount.delete(currentRoute);
      this.handleMaxRetriesReached(currentRoute);
    }
  }

  /**
   * Reintenta cargar el módulo
   * @param route Ruta del módulo
   */
  private retryModuleLoad(route: string): void {
    // Limpiar caché del navegador para chunks
    this.clearChunkCache();

    // Recargar la ruta actual
    this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
      this.router.navigate([route]);
    });
  }

  /**
   * Maneja el caso cuando se alcanza el máximo de reintentos
   * @param route Ruta del módulo
   */
  private handleMaxRetriesReached(route: string): void {
    this.toastService.error(
      'Error persistente cargando la página. Recargando la aplicación...',
      5000
    );

    // Esperar un poco antes de recargar completamente
    setTimeout(() => {
      this.forceReload();
    }, 2000);
  }

  /**
   * Limpia el caché de chunks del navegador
   */
  private clearChunkCache(): void {
    try {
      // Limpiar caché de módulos si está disponible
      if ('caches' in window) {
        caches.keys().then((cacheNames) => {
          cacheNames.forEach((cacheName) => {
            if (cacheName.includes('chunk') || cacheName.includes('js')) {
              caches.delete(cacheName);
            }
          });
        });
      }
    } catch (error) {
      console.warn('No se pudo limpiar el caché:', error);
    }
  }

  /**
   * Fuerza la recarga completa de la aplicación
   */
  private forceReload(): void {
    // Limpiar localStorage y sessionStorage relacionado con chunks
    this.clearStorageChunkData();

    // Recargar la página completamente
    window.location.reload();
  }

  /**
   * Limpia datos relacionados con chunks del almacenamiento
   */
  private clearStorageChunkData(): void {
    try {
      // Limpiar entradas relacionadas con chunks en localStorage
      const localStorageKeys = Object.keys(localStorage);
      localStorageKeys.forEach((key) => {
        if (
          key.includes('chunk') ||
          key.includes('module') ||
          key.includes('webpack')
        ) {
          localStorage.removeItem(key);
        }
      });

      // Limpiar entradas relacionadas con chunks en sessionStorage
      const sessionStorageKeys = Object.keys(sessionStorage);
      sessionStorageKeys.forEach((key) => {
        if (
          key.includes('chunk') ||
          key.includes('module') ||
          key.includes('webpack')
        ) {
          sessionStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.warn('No se pudo limpiar el almacenamiento:', error);
    }
  }

  /**
   * Resetea los contadores de reintentos
   */
  resetRetryCounters(): void {
    this.moduleRetryCount.clear();
    this.errorCountSubject.next(0);
  }

  /**
   * Obtiene estadísticas de errores
   * @returns Objeto con estadísticas
   */
  getErrorStats(): { totalErrors: number; routesWithErrors: string[] } {
    return {
      totalErrors: this.errorCountSubject.value,
      routesWithErrors: Array.from(this.moduleRetryCount.keys()),
    };
  }

  /**
   * Maneja manualmente un error de carga de módulo
   * @param route Ruta del módulo
   * @param error Error ocurrido
   */
  handleModuleLoadError(route: string, error: any): void {
    console.error(`Error cargando módulo para la ruta ${route}:`, error);
    this.handleChunkLoadError(error.toString());
  }
}
